/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IMaskInput } from "react-imask";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Select, RadioButton, FieldGroup, AsyncSelect } from "core/components";
import { toRangeOptions } from "core/helpers";
import { navigateTo } from "core/navigation";
import {
  CostItemFormData,
  InventoryItemFormData,
  RevisedTemplateIdMask,
  StandardTemplateIdMask,
  SubdivisionFormData,
  TaskFormData,
  TemplateFormData,
  TemplateSchema,
  TemplateStatus,
  TemplateTaskFormData,
  TemplateTaskItemFormData,
  UnitModelFormData,
  UnitType
} from "core/schema";
import {
  activate_admin_template_path,
  admin_templates_path,
  admin_template_path,
  approve_admin_template_path,
  create_for_approval_admin_templates_path,
  deactivate_admin_template_path,
  for_approval_admin_template_path,
  reject_admin_template_path
} from "routes";
import TemplateTasksForm from "./TemplateTasksForm";
import TemplateContext from "./TemplateContext";
import DocumentFields from "./DocumentFields";

type TemplateFormProps = {
  template: TemplateFormData;
  subdivisions: Array<SubdivisionFormData>;
  unit_models: Array<UnitModelFormData>;
  is_purchasing: boolean;
  tasks: Array<TaskFormData>;
  items: Array<InventoryItemFormData | CostItemFormData>;
};

const TemplateForm = ({ template, subdivisions, unit_models, is_purchasing, tasks, items }: TemplateFormProps): JSX.Element => {
  const formMethods = useForm<TemplateFormData>({
    mode: "onSubmit",
    defaultValues: TemplateSchema.cast(template),
    resolver: yupResolver(TemplateSchema)
  });
  const {
    control,
    register,
    unregister,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, isValidating, errors }
  } = formMethods;
  const status = watch("status");
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [templateTasks, setTemplateTasks] = React.useState([]);
  const [showApproveConfirmationModal, setShowApproveConfirmationModal] = React.useState(false);
  const [showRejectConfirmationModal, setShowRejectConfirmationModal] = React.useState(false);

  const { policy } = template;
  const subdivision = subdivisions.find((subdivision) => subdivision.id == template.subdivision_id);
  const unitModel = unit_models.find((unitModel) => unitModel.id == template.unit_model_id);
  const [templateId, setTemplateId] = React.useState<Record<string, string>>({
    subdivisionCode: subdivision?.code || "",
    unitModelCode: unitModel?.code || "",
    phase: template?.phase?.padStart(2, "0") || "",
    block: template.block?.padStart(2, "0") || "",
    lot: template.lot?.padStart(2, "0") || ""
  });
  const [phases, setPhases] = React.useState(subdivision ? toRangeOptions(subdivision.phase_start, subdivision.phase_end) : []);
  const [blocks, setBlocks] = React.useState(subdivision ? toRangeOptions(subdivision.block_start, subdivision.block_end) : []);
  const [lots, setLots] = React.useState(subdivision ? toRangeOptions(subdivision.lot_start, subdivision.lot_end) : []);
  const [technologies, setTechnologies] = React.useState([]);
  const [tasksForDestruction, setTasksForDestruction] = React.useState<Array<TemplateTaskFormData>>([]);
  const [itemsForDestruction, setItemsForDestruction] = React.useState<Array<TemplateTaskItemFormData>>([]);

  const buildTemplateId = (unitType: string, newTemplateId: Record<string, string>) => {
    if (unitType == UnitType.Standard) {
      return `STD-${newTemplateId.subdivisionCode}${newTemplateId.phase}${newTemplateId.unitModelCode}-`;
    } else {
      return `REV-${newTemplateId.subdivisionCode}${newTemplateId.phase}${newTemplateId.unitModelCode}-${newTemplateId.block}${newTemplateId.lot}`;
    }
  };

  const getTemplates = async (code = "") => {
    const page = 1;
    const per = 10;

    try {
      const response = await api.get(admin_templates_path({ format: "json", page, per, code }));
      setTemplates(response.data);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const loadTemplateOptions = useAsyncDebounce(getTemplates, 400);

  React.useEffect(() => {
    setTechnologies([
      { label: "N/A", value: "N/A" },
      { label: "CHB-Conventional", value: "CHB-Conventional" },
      { label: "AAC-Starken Conventional Footing", value: "AAC-Starken Conventional Footing" },
      { label: "AAC-Starken Matt Footing", value: "AAC-Starken Matt Footing" },
      { label: "AAC-Lightstrong Conventional Footing", value: "AAC-Lightstrong Conventional Footing" },
      { label: "Plaswall-Sterling", value: "Plaswall-Sterling" }
    ]);
  }, []);

  const withTasksForDestruction = (data) => {
    const { template_tasks_attributes } = data;

    for (let index = 0; index < template_tasks_attributes.length; index++) {
      const templateTask = template_tasks_attributes[index];
      const { template_task_items_attributes } = templateTask;
      const templateTaskItems = itemsForDestruction.filter((templateTaskItem) => templateTaskItem.template_task_id == templateTask.id);
      template_tasks_attributes[index] = {
        ...templateTask,
        template_task_items_attributes: [...template_task_items_attributes, ...templateTaskItems]
      };
    }

    return { ...data, template_tasks_attributes: [...template_tasks_attributes, ...tasksForDestruction] };
  };

  const save = async (data: TemplateFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_template_path({ id: template.id }), withTasksForDestruction(data));
      } else {
        await api.post(admin_templates_path({ id: template.id }), withTasksForDestruction(data));
      }
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const saveForApproval = async (data: TemplateFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(for_approval_admin_template_path({ id: template.id }), withTasksForDestruction(data));
      } else {
        await api.post(create_for_approval_admin_templates_path({ id: template.id }), withTasksForDestruction(data));
      }
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const approveTemplate = async (data: TemplateFormData): Promise<unknown> => {
    try {
      await api.put(approve_admin_template_path({ id: template.id }), data);
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowApproveConfirmationModal(!showApproveConfirmationModal);
    }
  };

  const rejectTemplate = async (data: TemplateFormData): Promise<unknown> => {
    try {
      await api.put(reject_admin_template_path({ id: template.id }), data);
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowRejectConfirmationModal(!showRejectConfirmationModal);
    }
  };

  const activateTemplate = async (data: TemplateFormData): Promise<unknown> => {
    try {
      await api.put(activate_admin_template_path({ id: template.id }), data);
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const deactivateTemplate = async (data: TemplateFormData): Promise<unknown> => {
    try {
      await api.put(deactivate_admin_template_path({ id: template.id }), data);
      navigateTo(admin_templates_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);

    if (data.template_tasks_attributes?.type == "sum") {
      setFormErrors([data.template_tasks_attributes?.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const actionsForDraft = () => {
    return (
      <>
        <Button
          className="button--brand mr-3"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="save"
          title="Save as Draft"
          onClick={() => {
            setValue("status", TemplateStatus.Draft);
            handleSubmit(async (data) => save(data), onError)();
          }}
        />
        <Button
          className="button--brand"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="check"
          title="Submit for Approval"
          onClick={() => {
            setValue("status", TemplateStatus.ForApproval);
            handleSubmit(async (data) => saveForApproval(data), onError)();
          }}
        />
      </>
    );
  };

  const actionsForApproval = () => {
    return (
      <>
        <Button
          className="button--brand mr-3"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="check-circle"
          title="Approve Template"
          onClick={() => {
            setShowApproveConfirmationModal(!showApproveConfirmationModal);
          }}
        />
        <Button
          className="button--danger"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="times-circle"
          title="Reject Template"
          onClick={() => {
            setShowRejectConfirmationModal(!showRejectConfirmationModal);
          }}
        />
      </>
    );
  };

  return (
    <React.StrictMode>
      <TemplateContext.Provider value={{ template, setTasksForDestruction, setItemsForDestruction }}>
        <FormProvider {...formMethods}>
          <Form errors={formErrors}>
            <Card>
              <Card.Header title={template.id ? "Edit Template" : "New Template"}>
                <Button
                  className="button--default mr-3"
                  disabled={isSubmitting || isValidating}
                  icon="arrow-left"
                  title="Back"
                  type="button"
                  onClick={() => navigateTo(admin_templates_path())}
                />
                {policy?.for_approval && !is_purchasing && actionsForDraft()}
                {policy?.approve && !is_purchasing && actionsForApproval()}
                {policy?.deactivate && !is_purchasing && (
                  <Button
                    className="button--danger"
                    loading={isSubmitting || isValidating}
                    disabled={isSubmitting || isValidating}
                    icon="check-circle"
                    title="Deactivate Template"
                    onClick={handleSubmit(async (data) => deactivateTemplate(data), onError)}
                  />
                )}
                {policy?.activate && !is_purchasing && (
                  <Button
                    className="button--brand"
                    loading={isSubmitting || isValidating}
                    disabled={isSubmitting || isValidating}
                    icon="check-circle"
                    title="Activate Template"
                    onClick={handleSubmit(async (data) => activateTemplate(data), onError)}
                  />
                )}
              </Card.Header>
              <Card.Body>
                <Container>
                  {!template.id && (
                    <Row>
                      <Col lg={6} className="px-4">
                        <Controller
                          name="template_id"
                          control={control}
                          render={({ field }) => (
                            <AsyncSelect
                              label="Template"
                              placeholder="Select a Template"
                              noOptionsMessage={() => "No available templates."}
                              defaultOptions
                              loadOptions={loadTemplateOptions}
                              getOptionLabel={(option) => option.code}
                              getOptionValue={(option) => option.id}
                              isOptionDisabled={(option) => option.status != TemplateStatus.Active}
                              onChange={(option, actionMeta) => {
                                field.onChange(option?.id);

                                setTemplateId({
                                  ...templateId,
                                  subdivisionCode: option?.subdivision?.code,
                                  unitModelCode: option?.unit_model?.code,
                                  phase: option?.phase?.padStart(2, "0") || "",
                                  block: option?.block?.padStart(2, "0") || "",
                                  lot: option?.lot?.padStart(2, "0") || ""
                                });

                                if (actionMeta.action == "select-option") {
                                  setSelectedTemplate(option);
                                  const templateTasks = option.template_tasks_attributes.map((templateTask) => {
                                    return {
                                      ...templateTask,
                                      id: null,
                                      template_id: null,
                                      template_task_items_attributes: templateTask.template_task_items_attributes.map((templateTaskItem) => {
                                        return { ...templateTaskItem, id: null, template_task_id: null };
                                      })
                                    };
                                  });

                                  setTemplateTasks(templateTasks);
                                  setPhases(toRangeOptions(option.subdivision.phase_start, option.subdivision.phase_end));
                                  setBlocks(toRangeOptions(option.subdivision.block_start, option.subdivision.block_end));
                                  setLots(toRangeOptions(option.subdivision.lot_start, option.subdivision.lot_end));

                                  reset({
                                    ...option,
                                    id: null,
                                    template_id: option.id,
                                    code: buildTemplateId(getValues("unit_type"), {
                                      subdivisionCode: option.subdivision.code,
                                      unitModelCode: option.unit_model.code,
                                      phase: option.phase.padStart(2, "0") || "",
                                      block: option.block?.padStart(2, "0") || "",
                                      lot: option.lot?.padStart(2, "0") || ""
                                    }),
                                    status: TemplateStatus.Draft,
                                    template_tasks_attributes: template.template_tasks_attributes
                                  });
                                } else {
                                  setSelectedTemplate(null);
                                  setTemplateTasks([]);
                                  setPhases([]);
                                  setBlocks([]);
                                  setLots([]);
                                  reset({
                                    unit_type: UnitType.Standard,
                                    documents_attributes: template.documents_attributes,
                                    template_tasks_attributes: template.template_tasks_attributes
                                  });
                                }
                                unregister("template_id");
                              }}
                              ref={field.ref}
                              value={templates.find((template) => template.id == field.value) || selectedTemplate || ""}
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="px-3">
                    <Col lg={6}>
                      <FieldGroup label="Unit Type">
                        <Row>
                          <Col lg={3}>
                            <RadioButton
                              {...register("unit_type")}
                              label="Standard"
                              required
                              errorMessage={errors.unit_type?.message}
                              value={UnitType.Standard}
                              disabled={template.status != TemplateStatus.Draft || is_purchasing}
                            />
                          </Col>
                          <Col lg={3}>
                            <RadioButton
                              {...register("unit_type")}
                              label="Revised"
                              required
                              errorMessage={errors.unit_type?.message}
                              value={UnitType.Revised}
                              disabled={template.status != TemplateStatus.Draft || is_purchasing}
                            />
                          </Col>
                        </Row>
                      </FieldGroup>
                    </Col>
                    <Col className="px-4">
                      <DocumentFields readOnly={template.status != TemplateStatus.Draft} />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={6} className="px-4">
                      <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Template ID" required errorMessage={errors.code?.message}>
                            <IMaskInput
                              mask={[
                                { mask: StandardTemplateIdMask, overwrite: true, autofix: true, lazy: true },
                                { mask: RevisedTemplateIdMask, overwrite: true, autofix: true, lazy: true }
                              ]}
                              inputRef={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              value={field.value}
                              className="field text-uppercase"
                              placeholder="STD-SCR04MIRANDA-0001"
                              placeholderChar="#"
                              readOnly={template.status != TemplateStatus.Draft || is_purchasing}
                            />
                          </FieldGroup>
                        )}
                      />
                      <Controller
                        name="subdivision_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Subdivision"
                            placeholder="Select Subdivision"
                            required
                            options={subdivisions}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                              setTemplateId({
                                ...templateId,
                                subdivisionCode: newOption?.code
                              });
                              setValue(
                                "code",
                                buildTemplateId(getValues("unit_type"), {
                                  ...templateId,
                                  subdivisionCode: newOption?.code
                                })
                              );
                              setPhases(toRangeOptions(newOption?.phase_start, newOption?.phase_end));
                              setBlocks(toRangeOptions(newOption?.block_start, newOption?.block_end));
                              setLots(toRangeOptions(newOption?.lot_start, newOption?.lot_end));
                            }}
                            value={subdivisions.find((subdivision) => subdivision.id == field.value) || null}
                            errorMessage={errors.subdivision_id?.message}
                            disabled={template.status != TemplateStatus.Draft || is_purchasing}
                          />
                        )}
                      />
                      <Row>
                        <Col>
                          <Controller
                            name="phase"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Phase"
                                placeholder="Select Phase"
                                required
                                options={phases}
                                noOptionsMessage={() => "Select a Subdivision first."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);
                                  setTemplateId({
                                    ...templateId,
                                    phase: newOption?.label
                                  });
                                  setValue(
                                    "code",
                                    buildTemplateId(getValues("unit_type"), {
                                      ...templateId,
                                      phase: newOption?.label
                                    })
                                  );
                                }}
                                value={phases.find((phase) => phase.value == field.value) || null}
                                errorMessage={errors.phase?.message}
                                disabled={template.status != TemplateStatus.Draft || is_purchasing}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="unit_model_id"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Unit Model"
                                placeholder="Select Unit Model"
                                required
                                options={unit_models}
                                getOptionLabel={(option) => `${option.name}`}
                                getOptionValue={(option) => option.id}
                                isOptionDisabled={(option) => !option.active}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.id);
                                  setTemplateId({
                                    ...templateId,
                                    unitModelCode: newOption.code
                                  });
                                  setValue(
                                    "code",
                                    buildTemplateId(getValues("unit_type"), {
                                      ...templateId,
                                      unitModelCode: newOption.code
                                    })
                                  );
                                }}
                                value={unit_models.find((unit_model) => unit_model.id == field.value) || null}
                                errorMessage={errors.unit_model_id?.message}
                                disabled={template.status != TemplateStatus.Draft || is_purchasing}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      {getValues("unit_type") == UnitType.Revised && (
                        <Row>
                          <Col>
                            <Controller
                              name="block"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Block"
                                  placeholder="Select Block"
                                  required
                                  options={blocks}
                                  noOptionsMessage={() => "Select a Subdivision first."}
                                  getOptionLabel={(option) => option.label}
                                  getOptionValue={(option) => option.value}
                                  onChange={(newOption) => {
                                    field.onChange(newOption?.value);
                                    setTemplateId({
                                      ...templateId,
                                      block: newOption?.label
                                    });
                                    setValue(
                                      "code",
                                      buildTemplateId(getValues("unit_type"), {
                                        ...templateId,
                                        block: newOption?.label
                                      })
                                    );
                                  }}
                                  value={blocks.find((block) => block.value == field.value) || null}
                                  errorMessage={errors.block?.message}
                                  disabled={template.status != TemplateStatus.Draft || is_purchasing}
                                />
                              )}
                            />
                          </Col>
                          <Col>
                            <Controller
                              name="lot"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  label="Lot"
                                  placeholder="Select Lot"
                                  required
                                  options={lots}
                                  noOptionsMessage={() => "Select a Subdivision first."}
                                  getOptionLabel={(option) => option.label}
                                  getOptionValue={(option) => option.value}
                                  onChange={(newOption) => {
                                    field.onChange(newOption?.value);
                                    setTemplateId({
                                      ...templateId,
                                      lot: newOption?.label
                                    });
                                    setValue(
                                      "code",
                                      buildTemplateId(getValues("unit_type"), {
                                        ...templateId,
                                        lot: newOption?.label
                                      })
                                    );
                                  }}
                                  value={lots.find((lot) => lot.value == field.value) || null}
                                  errorMessage={errors.lot?.message}
                                  disabled={template.status != TemplateStatus.Draft || is_purchasing}
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      )}
                    </Col>
                    <Col lg={6} className="px-4">
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Description" required={status != TemplateStatus.Draft} errorMessage={errors.description?.message}>
                            <textarea
                              {...field}
                              className="field mb-4"
                              placeholder="Use for sales closed January 1, 2021 onwards."
                              maxLength={500}
                              readOnly={template.status != TemplateStatus.Draft || is_purchasing}
                            />
                          </FieldGroup>
                        )}
                      />
                      <Row>
                        <Col>
                          <Controller
                            name="technology"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Technology"
                                placeholder="Select Technology"
                                required={status != TemplateStatus.Draft}
                                options={technologies}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);
                                }}
                                value={technologies.find((technology) => technology.value == field.value) || null}
                                errorMessage={errors.technology?.message}
                                disabled={template.status != TemplateStatus.Draft || is_purchasing}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="days_to_complete"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup
                                label="Days to Complete"
                                required={status != TemplateStatus.Draft}
                                errorMessage={errors.days_to_complete?.message}
                              >
                                <IMaskInput
                                  mask={Number}
                                  value={field.value?.toString()}
                                  unmask={true}
                                  radix="."
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  className="field"
                                  placeholder="365"
                                  maxLength="13"
                                  readOnly={template.status != TemplateStatus.Draft || is_purchasing}
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </Card>
            {template.status == TemplateStatus.Draft && (
              <TemplateTasksForm tasks={tasks} items={items} templateTasks={templateTasks} is_purchasing={is_purchasing} />
            )}
            <Modal dialogClassName="modal-dialog-centered" show={showApproveConfirmationModal}>
              <Modal.Header className="align-items-center no-gutters">
                <div className="tile">
                  <h4>Approve Template</h4>
                </div>
                <div className="tile-auto">
                  <button
                    aria-label="Close"
                    className="link-icon"
                    type="button"
                    onClick={() => {
                      setShowApproveConfirmationModal(!showApproveConfirmationModal);
                    }}
                  >
                    <div className="svg-icon">
                      <svg className="icon icon-times ">
                        <use xlinkHref="#times"></use>
                      </svg>
                    </div>
                  </button>
                </div>
              </Modal.Header>
              <Modal.Body>Are you sure you want to approve template?</Modal.Body>
              <Modal.Footer className="justify-content-center">
                <div className="tile-auto">
                  <a className="button button--brand" onClick={handleSubmit(async (data) => approveTemplate(data), onError)}>
                    Yes, approve template
                  </a>
                </div>
              </Modal.Footer>
            </Modal>
            <Modal dialogClassName="modal-dialog-centered" show={showRejectConfirmationModal}>
              <Modal.Header className="align-items-center no-gutters">
                <div className="tile">
                  <h4>Reject Template</h4>
                </div>
                <div className="tile-auto">
                  <button
                    aria-label="Close"
                    className="link-icon"
                    type="button"
                    onClick={() => {
                      setShowRejectConfirmationModal(!showRejectConfirmationModal);
                    }}
                  >
                    <div className="svg-icon">
                      <svg className="icon icon-times ">
                        <use xlinkHref="#times"></use>
                      </svg>
                    </div>
                  </button>
                </div>
              </Modal.Header>
              <Modal.Body>Are you sure you want to reject template?</Modal.Body>
              <Modal.Footer className="justify-content-center">
                <div className="tile-auto">
                  <a className="button button--danger" onClick={handleSubmit(async (data) => rejectTemplate(data), onError)}>
                    Yes, reject template
                  </a>
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        </FormProvider>
      </TemplateContext.Provider>
    </React.StrictMode>
  );
};

export default TemplateForm;
